import { useState, useEffect, useCallback, useRef } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Laugh, RefreshCw, Check, Loader2, Eye } from "lucide-react";

const VIBES = [
  { key: "oneliner", label: "One-liner" },
  { key: "bar", label: "Walks into a bar" },
  { key: "dialogue", label: "Witty dialogue" },
];

// One opt-in switch + (when ON) a live preview of a witty closing joke the creator can cycle & lock in.
// Renders as the FINAL card in the video, right before the end card (setup → punchline "chuckle" beat).
export const RiddleCardStudio = ({ manualId, orientation = "landscape", initial }) => {
  const [enabled, setEnabled] = useState(!!(initial && initial.enabled));
  const [locked, setLocked] = useState(
    initial && initial.setup && initial.punchline ? { setup: initial.setup, punchline: initial.punchline } : null
  );
  const [vibe, setVibe] = useState((initial && initial.vibe) || null);  // null = "surprise me"
  const [busy, setBusy] = useState(false);
  const [card, setCard] = useState(null); // { setup, punchline, image, answer_image }
  const [showPunch, setShowPunch] = useState(false);
  const [savedNow, setSavedNow] = useState(!!(initial && initial.setup && initial.punchline));
  const loadedOnce = useRef(false);

  const persist = useCallback(async (next) => {
    try {
      await api.post(`/manuals/${manualId}/riddle`, {
        enabled: !!next.enabled,
        setup: next.locked?.setup ?? "",
        punchline: next.locked?.punchline ?? "",
        vibe: next.vibe ?? null,
      });
    } catch {
      toast.error("Couldn't save your Riddle card setting — please try again");
    }
  }, [manualId]);

  const fetchPreview = useCallback(async (body) => {
    setBusy(true);
    setShowPunch(false);
    try {
      const { data } = await api.post(`/manuals/${manualId}/riddle/preview`, { orientation, ...body });
      setCard(data);
      return data;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't write a joke");
      return null;
    } finally {
      setBusy(false);
    }
  }, [manualId, orientation]);

  useEffect(() => {
    if (!enabled) {
      loadedOnce.current = false;
      setCard(null);
      setShowPunch(false);
      return;
    }
    if (loadedOnce.current) return;
    loadedOnce.current = true;
    if (locked && locked.setup && locked.punchline) {
      fetchPreview({ setup: locked.setup, punchline: locked.punchline }).then((d) => { if (d) setSavedNow(true); });
    } else {
      setSavedNow(false);
      fetchPreview({ vibe });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled]);

  const toggle = (val) => {
    setEnabled(val);
    persist({ enabled: val, locked, vibe });
  };

  const nextJoke = () => {
    setSavedNow(false);
    fetchPreview({ vibe });
  };

  const pickVibe = (v) => {
    const next = vibe === v ? null : v;  // tap again to clear back to "surprise me"
    setVibe(next);
    setSavedNow(false);
    persist({ enabled, locked, vibe: next });
    fetchPreview({ vibe: next });
  };

  const useThisJoke = () => {
    if (!card) return;
    const lk = { setup: card.setup, punchline: card.punchline };
    setLocked(lk);
    setSavedNow(true);
    persist({ enabled: true, locked: lk, vibe });
    toast.success("Riddle card locked in ✓");
  };

  return (
    <div data-testid="riddle-studio" className="mt-5 pt-5" style={{ borderTop: "1px solid #2a2536" }}>
      <p className="text-[11px] mb-3 px-1 leading-relaxed" style={{ color: "#a8a4ba" }}>
        Close on a laugh: drop a witty <b style={{ color: "#d9d5ea" }}>“Just For Fun” joke card</b> right before your end card —
        the setup holds, then the punchline pops. Smart, topical humor for grown-up viewers. <b style={{ color: "#d9d5ea" }}>Off by default</b>.
      </p>

      {/* Switch */}
      <button
        type="button"
        data-testid="riddle-toggle"
        onClick={() => toggle(!enabled)}
        className="w-full flex items-center justify-between gap-3 rounded-2xl border-2 px-4 py-3.5 transition-colors"
        style={enabled
          ? { borderColor: "#f5c518", background: "linear-gradient(135deg, rgba(245,197,24,.20), rgba(201,116,26,.12))", boxShadow: "0 0 0 1px rgba(245,197,24,.25)" }
          : { borderColor: "#7c5cff", background: "linear-gradient(135deg, #2b2545, #211c33)", boxShadow: "0 8px 22px -14px rgba(124,92,255,.9)" }}
      >
        <span className="flex items-center gap-2.5 text-left">
          <span className="flex items-center justify-center w-8 h-8 rounded-xl shrink-0"
            style={{ background: enabled ? "rgba(245,197,24,.22)" : "rgba(124,92,255,.20)" }}>
            <Laugh size={17} style={{ color: enabled ? "#f5c518" : "#f5c518" }} />
          </span>
          <span className="leading-tight">
            <span className="block font-extrabold text-[15px]" style={{ color: "#ffffff" }}>😄 Just For Fun (Riddle Card)</span>
            <span className="block text-[11px] font-semibold" style={{ color: enabled ? "#f5c518" : "#c3b9ff" }}>
              A closing joke — right before your end card
            </span>
          </span>
        </span>
        <span className="flex items-center gap-2 shrink-0">
          <span className="text-[11px] font-extrabold tracking-wide" style={{ color: enabled ? "#f5c518" : "#8f88ad" }}>
            {enabled ? "ON" : "OFF"}
          </span>
          <span className="relative inline-block w-11 h-6 rounded-full transition-colors" style={{ background: enabled ? "#c9741a" : "#4a4560" }}>
            <span className="absolute top-0.5 h-5 w-5 rounded-full bg-white shadow transition-all" style={{ left: enabled ? "22px" : "2px" }} />
          </span>
        </span>
      </button>

      {enabled && (
        <div className="rounded-2xl border-2 p-4 mt-2" style={{ borderColor: "#2a2536", background: "#0f0f16" }} data-testid="riddle-preview-wrap">
          {/* Joke style picker — tap a vibe (tap again to go back to surprise-me) */}
          <div className="flex items-center gap-1.5 flex-wrap mb-3" data-testid="riddle-vibe-picker">
            <span className="text-[11px] font-extrabold text-white/40 mr-0.5">Vibe</span>
            {VIBES.map((v) => {
              const on = vibe === v.key;
              return (
                <button
                  key={v.key}
                  type="button"
                  data-testid={`riddle-vibe-${v.key}`}
                  onClick={() => pickVibe(v.key)}
                  disabled={busy}
                  title={`Write the joke as a ${v.label.toLowerCase()}`}
                  className="px-2.5 py-1 rounded-full text-[11px] font-extrabold border transition-all disabled:opacity-50"
                  style={on
                    ? { background: "#f5c518", borderColor: "#f5c518", color: "#171426" }
                    : { background: "transparent", borderColor: "#3a4a34", color: "#cfe3c6" }}
                >
                  {v.label}
                </button>
              );
            })}
          </div>
          <div
            className="relative w-full mx-auto rounded-xl overflow-hidden bg-[#0f0f16] flex items-center justify-center"
            style={{ aspectRatio: orientation === "landscape" ? "16 / 9" : "9 / 16", maxWidth: orientation === "landscape" ? 460 : 236 }}
          >
            {busy && (
              <div className="absolute inset-0 flex items-center justify-center z-10 bg-black/40">
                <Loader2 className="animate-spin text-white" size={26} />
              </div>
            )}
            {card && (
              <img
                data-testid="riddle-preview-img"
                src={showPunch && card.answer_image ? card.answer_image : card.image}
                alt="Riddle card preview"
                className="w-full h-full object-cover"
              />
            )}
          </div>

          <div className="flex items-center justify-between mt-3">
            <span className="text-xs font-bold px-2.5 py-1 rounded-full text-[#111428]" style={{ background: "#f5c518" }}>
              Just For Fun
            </span>
            <button
              type="button"
              data-testid="riddle-peek-punchline"
              onClick={() => setShowPunch((v) => !v)}
              className="flex items-center gap-1.5 text-xs font-bold text-[#f5c518] hover:underline"
            >
              <Eye size={14} /> {showPunch ? "Show setup" : "Peek at punchline"}
            </button>
          </div>

          <div className="grid grid-cols-2 gap-2 mt-3">
            <button
              type="button"
              data-testid="riddle-next-btn"
              onClick={nextJoke}
              disabled={busy}
              className="flex items-center justify-center gap-2 rounded-xl border-2 px-3 py-2.5 font-bold text-sm transition-colors disabled:opacity-50"
              style={{ borderColor: "#1e3aaa", color: "#ffffff", background: "#1e3aaa" }}
            >
              <RefreshCw size={16} /> Another joke
            </button>
            <button
              type="button"
              data-testid="riddle-use-btn"
              onClick={useThisJoke}
              disabled={busy || !card || savedNow}
              className="flex items-center justify-center gap-2 rounded-xl px-3 py-2.5 font-bold text-sm transition-colors disabled:opacity-60"
              style={savedNow ? { background: "#1e6fff", color: "#fff" } : { background: "#e11d2a", color: "#fff" }}
            >
              <Check size={16} /> {savedNow ? "Locked in" : "Use this joke"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default RiddleCardStudio;
